import { useEffect, useState } from "react";
import { getNoctisReachability } from "../api/noctisReachability";
import http from "../api/apiClient";

const createPoller = (fetcher, { intervalMs = 60000 } = {}) => {
  let timer = null;
  let inflight = null;
  let state = {
    loading: false,
    error: null,
    data: null,
    meta: { httpStatus: null, latencyMs: null, updatedAt: null },
  };
  const listeners = new Set();

  const notify = () => listeners.forEach((fn) => fn(state));

  const setState = (patch) => {
    state = { ...state, ...patch };
    notify();
  };

  const tick = async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    if (inflight) return inflight;

    setState({ loading: true, error: null });
    const started = Date.now();
    inflight = (async () => {
      try {
        const res = await fetcher();
        const latencyMs = Date.now() - started;
        setState({
          loading: false,
          error: null,
          data: res.data,
          meta: {
            httpStatus: res.httpStatus ?? null,
            latencyMs,
            updatedAt: Date.now(),
          },
        });
      } catch (e) {
        const latencyMs = Date.now() - started;
        setState({
          loading: false,
          error: e?.message || String(e),
          meta: { httpStatus: e?.httpStatus ?? null, latencyMs, updatedAt: Date.now() },
        });
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  };

  const start = () => {
    if (timer) return;
    tick();
    timer = window.setInterval(tick, intervalMs);
  };

  const stop = () => {
    if (!timer) return;
    window.clearInterval(timer);
    timer = null;
  };

  const subscribe = (fn) => {
    listeners.add(fn);
    if (listeners.size === 1) start();
    fn(state);
    return () => {
      listeners.delete(fn);
      if (listeners.size === 0) stop();
    };
  };

  return { subscribe, tick };
};

const noctisPoller = createPoller(async () => {
  const data = await getNoctisReachability();
  return { data, httpStatus: null };
});

const giteaPoller = createPoller(async () => {
  const started = Date.now();
  const res = await http.get("/admin/gitea/issues/recent", { params: { limit: 1 } });
  const latencyMs = Date.now() - started;
  return {
    data: { configured: typeof res?.data?.configured === "boolean" ? res.data.configured : null },
    httpStatus: res?.status ?? null,
    latencyMs,
  };
});

const mailchimpPoller = createPoller(async () => {
  const res = await http.get("/admin/newsletter/recipients/mailchimp/status", {
    validateStatus: (code) => (code >= 200 && code < 300) || code === 204,
  });
  if (res?.status === 204) return { data: { neverSynced: true, ok: null }, httpStatus: 204 };
  const ok = typeof res?.data?.ok === "boolean" ? res.data.ok : null;
  return { data: { neverSynced: false, ok }, httpStatus: res?.status ?? null };
});

// e-Boekhouden: the backend reuses a live check for 5 minutes, so polling every 5 minutes is enough.
const eboekhoudenPoller = createPoller(async () => {
  const res = await http.get("/finance/settings/eboekhouden/status");
  return { data: res?.data ?? null, httpStatus: res?.status ?? null };
}, { intervalMs: 300000 });

/** Check the e-Boekhouden status again right away (for example after switching the connection on or off). */
export const refreshEBoekhoudenStatus = () => eboekhoudenPoller.tick();

const usePolledState = (poller, enabled = true) => {
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    return poller.subscribe(setSnapshot);
  }, [poller, enabled]);

  return snapshot;
};

export const useNoctisReachabilityPolled = (enabled = true) => usePolledState(noctisPoller, enabled);
export const useGiteaConfiguredPolled = (enabled = true) => usePolledState(giteaPoller, enabled);
export const useMailchimpHealthPolled = (enabled = true) => usePolledState(mailchimpPoller, enabled);
export const useEBoekhoudenStatusPolled = (enabled = true) => usePolledState(eboekhoudenPoller, enabled);
